import * as THREE from 'three';
import { TrackElement } from './TrackElement';
import { TrainingTrackElement } from '../../../../../shared/src/trainingTrack.types';
import { TRAINING_TRACK_CONFIG } from '../config/TrainingTrackConfig';

/**
 * Class representing a fire ring element in the training track
 */
export class FireRingElement extends TrackElement {
    private ringMesh: THREE.Mesh | null = null;
    private particles: THREE.Points | null = null;
    private particlePositions: Float32Array | null = null;
    
    /**
     * Constructor
     * @param element The fire ring element data
     */
    constructor(element: TrainingTrackElement) {
        super(element);
    }
    
    /**
     * Creates the Three.js object for this fire ring
     * @returns The created fire ring object
     */
    protected createObject(): THREE.Object3D {
        const ringRadius = this.properties.ringRadius || 3;
        const fireIntensity = this.properties.fireIntensity || 1.0;
        
        // Create a torus for the ring with more detailed geometry
        const torusGeometry = new THREE.TorusGeometry(ringRadius, 0.4, 24, 48);
        const torusMaterial = new THREE.MeshStandardMaterial({ 
            color: TRAINING_TRACK_CONFIG.FIRE_RING.COLOR,
            emissive: TRAINING_TRACK_CONFIG.FIRE_RING.COLOR,
            emissiveIntensity: 0.8,
            metalness: 0.7,
            roughness: 0.3
        });
        
        this.ringMesh = new THREE.Mesh(torusGeometry, torusMaterial);
        this.ringMesh.castShadow = true;
        
        // Create fire particles with more particles for a more intense effect
        const particleCount = Math.floor(TRAINING_TRACK_CONFIG.FIRE_RING.PARTICLE_COUNT * fireIntensity);
        const particleGeometry = new THREE.BufferGeometry();
        
        // Use a more vibrant color for the particles
        const particleMaterial = new THREE.PointsMaterial({
            color: TRAINING_TRACK_CONFIG.FIRE_RING.COLOR,
            size: TRAINING_TRACK_CONFIG.FIRE_RING.PARTICLE_SIZE,
            transparent: true,
            blending: THREE.AdditiveBlending,
            depthWrite: false // Poprawia wygląd nakładających się cząsteczek
        });
        
        // Create particle positions around the ring with more variation
        this.particlePositions = new Float32Array(particleCount * 3);
        for (let i = 0; i < particleCount; i++) {
            const angle = (i / particleCount) * Math.PI * 2;
            // Dodaj więcej losowości do promienia dla bardziej naturalnego wyglądu
            const radius = ringRadius + (Math.random() - 0.5) * 0.8;
            
            this.particlePositions[i * 3] = Math.cos(angle) * radius;
            // Zwiększ wysokość płomieni zgodnie z konfiguracją
            this.particlePositions[i * 3 + 1] = (Math.random() - 0.3) * TRAINING_TRACK_CONFIG.FIRE_RING.FLAME_HEIGHT * fireIntensity;
            this.particlePositions[i * 3 + 2] = Math.sin(angle) * radius;
        }
        
        particleGeometry.setAttribute('position', new THREE.BufferAttribute(this.particlePositions, 3));
        this.particles = new THREE.Points(particleGeometry, particleMaterial);
        
        // Dodaj światło punktowe dla efektu świecenia
        const fireLight = new THREE.PointLight(
            TRAINING_TRACK_CONFIG.FIRE_RING.COLOR,
            TRAINING_TRACK_CONFIG.FIRE_RING.LIGHT_INTENSITY * fireIntensity,
            TRAINING_TRACK_CONFIG.FIRE_RING.LIGHT_DISTANCE
        );
        
        // Create a group to hold the ring, particles and light
        const group = new THREE.Group();
        group.add(this.ringMesh);
        group.add(this.particles);
        group.add(fireLight);
        
        return group;
    }
    
    /**
     * Updates the fire ring animation
     * @param deltaTime Time elapsed since last update in seconds
     */
    public update(deltaTime: number): void {
        if (!this.particles || !this.particlePositions) return;
        
        const particleCount = TRAINING_TRACK_CONFIG.FIRE_RING.PARTICLE_COUNT;
        
        for (let i = 0; i < particleCount; i++) {
            // Make particles move upward and flicker
            this.particlePositions[i * 3 + 1] += (Math.random() * 0.1 - 0.05) * deltaTime * 10;
            
            // Reset particles that go too high
            if (this.particlePositions[i * 3 + 1] > TRAINING_TRACK_CONFIG.FIRE_RING.FLAME_HEIGHT) {
                this.particlePositions[i * 3 + 1] = -TRAINING_TRACK_CONFIG.FIRE_RING.FLAME_HEIGHT / 2;
            }
        }
        
        this.particles.geometry.attributes.position.needsUpdate = true;
    }
    
    /**
     * Handles player interaction with this fire ring
     * @param playerPosition The player's position
     * @param playerTank The player's tank
     * @param distance The distance to the player
     * @returns True if interaction was handled, false otherwise
     */
    public handleInteraction(playerPosition: THREE.Vector3, playerTank: any, distance: number): boolean {
        // Check if player is passing through the ring
        const ringRadius = this.properties.ringRadius || 3;
        
        if (distance < ringRadius * 1.5) {
            // Player is close to the ring, check if they're passing through it
            // This would require more complex collision detection in a real implementation
            // For now, we'll just return true if they're close enough
            return true;
        }
        
        return false;
    }
}